import { withApollo } from 'components/withApollo'
import { GetStaticPathsResult, NextPage } from 'next'
import createApolloClient from 'components/withApollo/createApolloClient'
import getStaticApolloProps from 'lib/getStaticApolloProps'
import { useRouter } from 'next/router'
import React from 'react'
import PageError from 'components/pages/PageError'
import Loading from 'components/Loading'
import PageMeta from 'components/PageMeta'
import PageLayout from 'components/pages/PageLayout'
import { ClientLogos } from 'components/ClientClouds'
import StatsSection from 'components/StatsSection'
import FeatureSection from 'components/pages/CateringMarketPage/FeatureSection'
import replaceCategoryVariables from 'components/pages/CateringPage/replaceCategoryVariables'
import NotFoundPage from 'components/pages/NotFoundPage/NotFoundPage'
import CateringRatings from 'components/CateringRatings'
import cn from 'classnames'
import TruckList from 'components/pages/CateringFoodTypePage/TruckList'

import Image from '../../Image'

import CtaSection from './CtaSection'
import ContentSections from './ContentSections'
import HeroSection from './HeroSection'
import FOOD_TYPES_PATHS_QUERY from './foodTypesPathsQuery'
import { foodTypesPathsQuery as foodTypesPathsQueryType } from './types/foodTypesPathsQuery'
import { useCateringFoodTypeQuery } from './types/query'
import { useCateringFoodTypeMarketQuery } from './types/CateringFoodTypeMarketQuery'
import { useCateringFoodTypeStructuredDataQuery } from './types/CateringFoodTypeStructuredDataQuery'
import FaqSection from './FaqSection'

type Props = {}

type Params = {
  foodType?: string
  market?: string
}

function CateringFoodTypePage() {
  const router = useRouter()
  const { foodType: foodTypeSlug, market: marketSlug } = (router.query ??
    {}) as Params

  const { data, error, loading } = useCateringFoodTypeQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      seoName: foodTypeSlug!,
      marketSeoName: marketSlug ?? null
    }
  })
  const {
    data: marketData,
    error: marketError,
    loading: marketLoading
  } = useCateringFoodTypeMarketQuery({
    skip: !marketSlug,
    fetchPolicy: 'cache-and-network',
    variables: {
      seoName: marketSlug!
    }
  })
  const { data: structuredData } = useCateringFoodTypeStructuredDataQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      categorySeoName: foodTypeSlug!,
      marketSeoName: marketSlug ?? null
    }
  })
  if (error ?? marketError)
    return <PageError>{(error ?? marketError)!.message}</PageError>

  if (!data?.foodTypePage && loading) return <Loading className="w-full h-72" />

  const market = marketSlug ? marketData?.marketBySlug : null

  if (marketSlug && !marketData?.marketBySlug && marketLoading)
    return <Loading className="w-full h-72" />

  const { foodTypePage } = data!

  if (!foodTypePage || !foodTypePage.active)
    return <NotFoundPage search={`${marketSlug || ''}`} />

  return (
    <>
      <PageMeta
        canonicalPath={
          marketSlug
            ? `/food-trucks/catering/${marketSlug}/cuisine/${foodTypeSlug}`
            : `/food-trucks/catering/cuisine/${foodTypeSlug}`
        }
        coordinates={market ? [market.latitude, market.longitude] : null}
        description={replaceCategoryVariables({
          text: foodTypePage.metaDescription,
          market: market?.name
        })}
        image={foodTypePage.image?.small}
        ldJson={structuredData?.structuredData?.cateringFoodTypePage}
        noIndex={!foodTypePage.active}
        title={`Best Food Trucks | ${replaceCategoryVariables({
          text: foodTypePage.title,
          market: market?.name
        })}`}
      />
      <PageLayout fullWidth>
        <div className="w-full">
          <HeroSection foodTypePage={foodTypePage} market={market} />
          <ClientLogos
            clients={[
              'pfizer',
              'boeing',
              'apple',
              'amazon',
              'cushman',
              'aramark',
              'compass',
              'swedish'
            ]}
          />
          <StatsSection
            description="By managing food truck daily life from event booking, to online ordering and catering we have cultivated an intimate relationship with great vendors throughout the country and know exactly which truck(s) will suit your needs, from a small neighborhood party to massive corporate events."
            points={[
              {
                label: 'Cities',
                value: '1,900+'
              },
              {
                label: 'Caterings',
                value: '2,000+'
              },
              {
                label: 'Attendees',
                value: '300,000+'
              },
              {
                label: 'Cuisines',
                value: '80+'
              }
            ]}
            title="Best Catering Expertise"
          />
          <ContentSections foodTypePage={foodTypePage} market={market} />
          <div className="sm:py-16 lg:py-0 mb-24 pr-2 pl-2">
            <div className="max-w-4xl mx-auto text-center mb-12 md:mb-16">
              <h2 className="text-3xl font-extrabold sm:text-4xl">
                To give you a taste
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-x-4 lg:gap-x-6 gap-y-4 lg:gap-y-0">
              {foodTypePage.foodTypePageItems.map((item, index) => (
                <div
                  key={item.id}
                  className={cn('relative', { 'hidden lg:block': index >= 6 })}
                >
                  <div className="shadow rounded-lg flex overflow-hidden">
                    <Image
                      alt={item.name}
                      className="object-cover w-full object-center"
                      height={600}
                      src={item.image.url}
                      width={600}
                    />
                  </div>
                  <div>
                    <p className="mt-2 mb-2 font-medium text-md lg:text-lg text-center text-gray-800">
                      {item.name}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <TruckList
            cuisineType={foodTypePage.name}
            marketName={market?.name}
            trucks={foodTypePage.trucks}
          />
          <CtaSection
            foodTypeName={foodTypePage.name}
            marketName={market?.name}
          />
          <FeatureSection />
          <CateringRatings foodTypeSlug={foodTypeSlug} marketId={market?.id} />
          <FaqSection foodTypePage={foodTypePage} />
        </div>
      </PageLayout>
    </>
  )
}

export default withApollo({ ssr: false })(CateringFoodTypePage as NextPage)

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = createApolloClient({}, null)

  const { data } = await client.query<foodTypesPathsQueryType>({
    query: FOOD_TYPES_PATHS_QUERY
  })

  const paths = data.customerPaths.cateringFoodTypesPaths.map(
    (cateringPath) => ({
      params: { foodType: cateringPath }
    })
  )

  return {
    paths,
    fallback: 'blocking'
  }
}

export async function getStaticPathsMarket(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking'
  }
}

export const getStaticProps = getStaticApolloProps<Props, Params>(
  CateringFoodTypePage,
  {
    revalidate: 60 * 60 // 1 hour
  }
)
